import React, { useState } from 'react';
import { PRIORITY_CONFIG, CATEGORY_CONFIG, STATUS_CONFIG } from './taskConfigs';
import { TEAM_TASKS_PER_PAGE } from './TaskCard';
import { MultiSelect } from './MultiSelect';
import { TaskCard } from './TaskCard';
import { Search, Filter, ChevronLeft, ChevronRight, ChevronDown, X, Loader2, Users } from 'lucide-react';

const ROLE_COLORS = {
  'Admin': { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', dot: 'bg-purple-500', cardBorder: 'border-t-purple-500', avatarBg: 'bg-purple-100', avatarText: 'text-purple-700' },
  'Company Admin': { bg: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-700', dot: 'bg-indigo-500', cardBorder: 'border-t-indigo-500', avatarBg: 'bg-indigo-100', avatarText: 'text-indigo-700' },
  'Manager': { bg: 'bg-sky-50', border: 'border-sky-200', text: 'text-sky-700', dot: 'bg-sky-500', cardBorder: 'border-t-sky-500', avatarBg: 'bg-sky-100', avatarText: 'text-sky-700' },
  'Sales Rep': { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', dot: 'bg-emerald-500', cardBorder: 'border-t-emerald-500', avatarBg: 'bg-emerald-100', avatarText: 'text-emerald-700' },
  'Executive': { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', dot: 'bg-amber-500', cardBorder: 'border-t-amber-500', avatarBg: 'bg-amber-100', avatarText: 'text-amber-700' },
  'Accounts': { bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-700', dot: 'bg-rose-500', cardBorder: 'border-t-rose-500', avatarBg: 'bg-rose-100', avatarText: 'text-rose-700' },
  'Viewer': { bg: 'bg-cyan-50', border: 'border-cyan-200', text: 'text-cyan-700', dot: 'bg-cyan-500', cardBorder: 'border-t-cyan-500', avatarBg: 'bg-cyan-100', avatarText: 'text-cyan-700' },
  'Unassigned': { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-500', dot: 'bg-gray-400', cardBorder: 'border-t-gray-400', avatarBg: 'bg-gray-100', avatarText: 'text-gray-500' },
};
const defaultColor = { bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-700', dot: 'bg-slate-500', cardBorder: 'border-t-slate-500', avatarBg: 'bg-slate-100', avatarText: 'text-slate-700' };

// Preferred role display order
const ROLE_ORDER = ['Company Admin', 'Admin', 'Manager', 'Sales Rep', 'Executive', 'Accounts', 'Viewer', 'Unassigned'];

const sortTasksAsc = (tasks) => {
  return [...tasks].sort((a, b) => {
    const dateA = (a.due_date || '9999-12-31') + ' ' + (a.due_time || '23:59');
    const dateB = (b.due_date || '9999-12-31') + ' ' + (b.due_time || '23:59');
    return dateA.localeCompare(dateB);
  });
};

export const TeamView = ({
  teamData, teamSearch, setTeamSearch, showTeamFilters, setShowTeamFilters,
  teamFilterRoles, setTeamFilterRoles, teamFilterUsers, setTeamFilterUsers,
  teamFilterStatuses, setTeamFilterStatuses, teamFilterPriorities, setTeamFilterPriorities,
  teamFilterCategories, setTeamFilterCategories, teamFilterDateFrom, setTeamFilterDateFrom,
  teamFilterDateTo, setTeamFilterDateTo, expandedUsers, setExpandedUsers,
  taskCardProps,
}) => {
  if (!teamData) return <div className="text-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400 mx-auto" /></div>;

  const allRoles = Object.keys(teamData);
  const allMembers = allRoles.flatMap(r => (teamData[r] || []).map(m => m.user));

  const q = teamSearch.toLowerCase().trim();
  const hasAnyFilter = q || teamFilterStatuses.length || teamFilterPriorities.length || teamFilterCategories.length || teamFilterUsers.length || teamFilterRoles.length || teamFilterDateFrom || teamFilterDateTo;

  // Build role-grouped members with filtered tasks
  const roleGrouped = {};
  let totalFilteredTasks = 0;
  let totalFilteredMembers = 0;

  for (const [role, members] of Object.entries(teamData)) {
    if (teamFilterRoles.length && !teamFilterRoles.includes(role)) continue;
    const filteredMembers = [];
    for (const member of members) {
      if (teamFilterUsers.length && !teamFilterUsers.includes(member.user.id)) continue;
      const filtered = member.tasks.filter(t => {
        if (q) {
          const haystack = [t.title, t.description, t.linked_lead_name, CATEGORY_CONFIG[t.category]?.label, t.category, ...(t.tags || []), t.assigned_to?.name, t.completion_remarks].filter(Boolean).join(' ').toLowerCase();
          if (!haystack.includes(q)) return false;
        }
        if (teamFilterStatuses.length && !teamFilterStatuses.includes(t.status)) return false;
        if (teamFilterPriorities.length && !teamFilterPriorities.includes(t.priority)) return false;
        if (teamFilterCategories.length && !teamFilterCategories.includes(t.category)) return false;
        if (teamFilterDateFrom && t.due_date && t.due_date < teamFilterDateFrom) return false;
        if (teamFilterDateTo && t.due_date && t.due_date > teamFilterDateTo) return false;
        return true;
      });
      if (filtered.length > 0) {
        const sortedTasks = sortTasksAsc(filtered);
        const today = new Date().toISOString().split('T')[0];
        filteredMembers.push({
          user: member.user,
          tasks: sortedTasks,
          stats: {
            total: filtered.length,
            pending: filtered.filter(t => ['pending', 'in_progress'].includes(t.status)).length,
            completed: filtered.filter(t => t.status === 'completed').length,
            overdue: filtered.filter(t => t.due_date && t.due_date < today && !['completed', 'not_completed'].includes(t.status)).length,
          },
        });
        totalFilteredTasks += filtered.length;
        totalFilteredMembers++;
      }
    }
    if (filteredMembers.length > 0) {
      roleGrouped[role] = filteredMembers;
    }
  }

  // Sort roles by preferred order
  const sortedRoles = Object.keys(roleGrouped).sort((a, b) => {
    const ia = ROLE_ORDER.indexOf(a);
    const ib = ROLE_ORDER.indexOf(b);
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
  });

  const clearTeamFilters = () => {
    setTeamSearch(''); setTeamFilterStatuses([]); setTeamFilterPriorities([]);
    setTeamFilterCategories([]); setTeamFilterUsers([]); setTeamFilterRoles([]);
    setTeamFilterDateFrom(''); setTeamFilterDateTo('');
  };
  const activeFilterCount = [teamFilterStatuses.length, teamFilterPriorities.length, teamFilterCategories.length, teamFilterUsers.length, teamFilterRoles.length, teamFilterDateFrom, teamFilterDateTo].filter(Boolean).length;

  return (
    <div data-testid="team-view">
      {/* Search + Filter Bar */}
      <div className="mb-4 space-y-2">
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
            <input value={teamSearch} onChange={e => setTeamSearch(e.target.value)} placeholder="Search by task title, description, lead name, tag..." className="w-full pl-9 pr-3 py-2 border rounded-xl text-sm bg-white focus:ring-2 focus:ring-amber-200" data-testid="team-search" />
          </div>
          <button onClick={() => setShowTeamFilters(!showTeamFilters)} className={`flex items-center gap-1.5 px-3 py-2 border rounded-xl text-xs font-medium shrink-0 transition-all duration-180 ease-out ${showTeamFilters || activeFilterCount > 0 ? 'bg-amber-50 text-amber-700 border-amber-300' : 'bg-white text-gray-600 hover:bg-amber-50/60'}`} data-testid="team-filter-toggle">
            <Filter className="w-3.5 h-3.5" /> Filters
            {activeFilterCount > 0 && <span className="text-white text-[9px] w-4 h-4 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #D97706 0%, #EA580C 100%)' }}>{activeFilterCount}</span>}
          </button>
        </div>

        {showTeamFilters && (
          <div className="bg-white border rounded-xl p-3 space-y-2" data-testid="team-filters-panel">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              <MultiSelect label="Role" testId="team-filter-role" options={allRoles.map(r => ({ value: r, label: r }))} selected={teamFilterRoles} onChange={setTeamFilterRoles} />
              <MultiSelect label="Member" testId="team-filter-user" options={allMembers.map(u => ({ value: u.id, label: u.name }))} selected={teamFilterUsers} onChange={setTeamFilterUsers} renderLabel={(id) => allMembers.find(u => u.id === id)?.name || id} />
              <MultiSelect label="Status" testId="team-filter-status" options={Object.entries(STATUS_CONFIG).map(([k, v]) => ({ value: k, label: v.label }))} selected={teamFilterStatuses} onChange={setTeamFilterStatuses} renderLabel={(k) => STATUS_CONFIG[k]?.label || k} />
              <MultiSelect label="Priority" testId="team-filter-priority" options={Object.entries(PRIORITY_CONFIG).map(([k, v]) => ({ value: k, label: v.label }))} selected={teamFilterPriorities} onChange={setTeamFilterPriorities} renderLabel={(k) => PRIORITY_CONFIG[k]?.label || k} />
              <MultiSelect label="Category" testId="team-filter-category" options={Object.entries(CATEGORY_CONFIG).map(([k, v]) => ({ value: k, label: `${v.icon} ${v.label}` }))} selected={teamFilterCategories} onChange={setTeamFilterCategories} renderLabel={(k) => CATEGORY_CONFIG[k]?.label || k} />
              <div>
                <label className="text-[10px] text-gray-500 mb-0.5 block font-medium">Due From</label>
                <input type="date" value={teamFilterDateFrom} onChange={e => setTeamFilterDateFrom(e.target.value)} className="w-full px-2 py-1.5 border rounded-lg text-xs" data-testid="team-filter-date-from" />
              </div>
              <div>
                <label className="text-[10px] text-gray-500 mb-0.5 block font-medium">Due To</label>
                <input type="date" value={teamFilterDateTo} onChange={e => setTeamFilterDateTo(e.target.value)} className="w-full px-2 py-1.5 border rounded-lg text-xs" data-testid="team-filter-date-to" />
              </div>
              <div className="flex items-end">
                <button onClick={clearTeamFilters} className="px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 rounded-lg border border-red-200 w-full" data-testid="team-clear-filters"><X className="w-3 h-3 inline mr-1" />Clear All</button>
              </div>
            </div>
            {hasAnyFilter && (
              <div className="flex flex-wrap items-center gap-1.5 pt-2 border-t">
                <span className="text-[10px] text-gray-400">{totalFilteredTasks} tasks, {totalFilteredMembers} members</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Role-Grouped Columnar View */}
      {sortedRoles.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-gray-300">
          <Search className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">No members with tasks found</p>
          {hasAnyFilter && <button onClick={clearTeamFilters} className="mt-2 px-4 py-1.5 text-xs text-amber-700 hover:bg-amber-50 rounded-lg">Clear Filters</button>}
        </div>
      ) : (
        <div className="space-y-6" data-testid="team-role-groups">
          {sortedRoles.map(role => {
            const rc = ROLE_COLORS[role] || defaultColor;
            const members = roleGrouped[role];
            const totalRoleTasks = members.reduce((s, m) => s + m.stats.total, 0);

            return (
              <div key={role} data-testid={`role-group-${role.replace(/\s/g, '-')}`}>
                {/* Role Header */}
                <div className={`flex items-center gap-3 mb-3 px-3 py-2 rounded-xl ${rc.bg} border ${rc.border}`}>
                  <div className={`w-3 h-3 rounded-full ${rc.dot}`} />
                  <h3 className={`font-bold text-base ${rc.text} uppercase tracking-wide flex-1`}>{role}</h3>
                  <div className="flex items-center gap-3 text-xs">
                    <span className={`${rc.text} font-medium`}><Users className="w-3.5 h-3.5 inline mr-1" />{members.length} members</span>
                    <span className={`${rc.text} font-medium`}>{totalRoleTasks} tasks</span>
                  </div>
                </div>

                {/* Member Columns under this Role */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {members.map(member => {
                    const uid = member.user.id;
                    const pageKey = `team_${uid}_page`;
                    const memberPage = expandedUsers[pageKey] || 1;
                    const totalPages = Math.ceil(member.tasks.length / TEAM_TASKS_PER_PAGE);
                    const start = (memberPage - 1) * TEAM_TASKS_PER_PAGE;
                    const pagedTasks = member.tasks.slice(start, start + TEAM_TASKS_PER_PAGE);

                    return (
                      <div key={uid} className={`bg-white rounded-2xl border border-gray-200 border-l-4 ${rc.cardBorder} flex flex-col`} data-testid={`team-member-${uid}`}>
                        {/* Member Header + Pagination */}
                        <div className="px-4 py-3 flex items-center gap-3">
                          <div className={`w-9 h-9 rounded-full ${rc.avatarBg} flex items-center justify-center text-sm font-bold ${rc.avatarText} shrink-0`}>{(member.user.name || '?')[0].toUpperCase()}</div>
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-base text-gray-800 truncate">{member.user.name}</div>
                            <div className="text-xs text-gray-400 truncate">{member.user.email}</div>
                          </div>
                          {totalPages > 0 && (
                            <div className="flex items-center gap-1.5 shrink-0" data-testid={`team-pagination-${uid}`}>
                              <span className="text-[10px] text-gray-400">{start + 1}-{Math.min(start + TEAM_TASKS_PER_PAGE, member.tasks.length)} of {member.tasks.length}</span>
                              <button disabled={memberPage <= 1} onClick={() => setExpandedUsers(p => ({ ...p, [pageKey]: memberPage - 1 }))} className="p-1 bg-gray-50 border rounded disabled:opacity-30 hover:bg-gray-100" data-testid={`team-prev-${uid}`}>
                                <ChevronLeft className="w-3 h-3" />
                              </button>
                              <span className="text-[10px] font-medium text-gray-600">{memberPage}/{totalPages}</span>
                              <button disabled={memberPage >= totalPages} onClick={() => setExpandedUsers(p => ({ ...p, [pageKey]: memberPage + 1 }))} className="p-1 bg-gray-50 border rounded disabled:opacity-30 hover:bg-gray-100" data-testid={`team-next-${uid}`}>
                                <ChevronRight className="w-3 h-3" />
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Stats */}
                        <div className="grid grid-cols-4 gap-1 mx-4 mb-2 bg-gray-50 rounded-xl p-2">
                          <div className="text-center"><div className="text-sm font-bold text-gray-700">{member.stats.total}</div><div className="text-[9px] text-gray-400">Total</div></div>
                          <div className="text-center"><div className="text-sm font-bold text-blue-600">{member.stats.pending}</div><div className="text-[9px] text-blue-400">Pending</div></div>
                          <div className="text-center"><div className="text-sm font-bold text-green-600">{member.stats.completed}</div><div className="text-[9px] text-green-400">Done</div></div>
                          <div className="text-center"><div className="text-sm font-bold text-red-600">{member.stats.overdue}</div><div className="text-[9px] text-red-400">Overdue</div></div>
                        </div>

                        {/* Task Cards */}
                        <div className="flex-1 px-3 pb-3 space-y-1.5">
                          {pagedTasks.map((task, idx) => (
                            <TaskCard key={task.id} task={task} compact serialNum={start + idx + 1} {...taskCardProps} />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
